if (!customElements.get("article-slider")) {
  class ArticleSlider extends HTMLElement {
    constructor() {
      super();

      this.swiper = null;

      this.handleMouseEnter =
        this.handleMouseEnter.bind(this);

      this.handleMouseLeave =
        this.handleMouseLeave.bind(this);

      this.handleBlockSelect =
        this.handleBlockSelect.bind(this);
    }

    connectedCallback() {
      if (this.swiper) return;

      this.slider = this.querySelector(
        "[data-article-slider-swiper]"
      );

      this.previousButton = this.querySelector(
        "[data-article-slider-prev]"
      );

      this.nextButton = this.querySelector(
        "[data-article-slider-next]"
      );

      this.pagination = this.querySelector(
        "[data-article-slider-pagination]"
      );

      this.progressBar = this.querySelector(
        "[data-article-slider-progress]"
      );

      if (!this.slider) return;

      document.addEventListener(
        "shopify:block:select",
        this.handleBlockSelect
      );

      this.initializeWhenSwiperIsReady();
    }

    initializeWhenSwiperIsReady(attempt = 0) {
      if (!this.isConnected || this.swiper) return;

      if (typeof window.Swiper === "function") {
        this.initializeSlider();
        return;
      }

      if (attempt >= 40) {
        console.warn(
          "Article slider: Swiper library is not available."
        );
        return;
      }

      this.swiperTimer = window.setTimeout(() => {
        this.initializeWhenSwiperIsReady(attempt + 1);
      }, 100);
    }

    initializeSlider() {
      const slideCount = this.slider.querySelectorAll(
        ".swiper-slide"
      ).length;

      if (!slideCount) return;

      const slidesDesktop = Number(
        this.dataset.slidesDesktop || 3
      );

      const slidesTablet = Number(
        this.dataset.slidesTablet || 2
      );

      const slidesMobile = Number(
        this.dataset.slidesMobile || 1.1
      );

      const gapDesktop = Number(
        this.dataset.gapDesktop || 22
      );

      const gapMobile = Number(
        this.dataset.gapMobile || 14
      );

      const sliderSpeed = Number(
        this.dataset.sliderSpeed || 500
      );

      const enableLoop =
        this.dataset.enableLoop === "true";

      const enableDrag =
        this.dataset.enableDrag === "true";

      const enableKeyboard =
        this.dataset.enableKeyboard === "true";

      const autoRotate =
        this.dataset.autoRotate === "true";

      const autoRotateDelay = Number(
        this.dataset.autoRotateDelay || 5000
      );

      const options = {
        slidesPerView: slidesMobile,
        slidesPerGroup: 1,
        spaceBetween: gapMobile,
        speed: sliderSpeed,

        loop:
          enableLoop &&
          slideCount > Math.ceil(slidesDesktop),

        grabCursor: enableDrag,
        allowTouchMove: enableDrag,
        simulateTouch: enableDrag,

        watchOverflow: true,
        observer: true,
        observeParents: true,
        observeSlideChildren: true,

        mousewheel: {
          forceToAxis: true
        },

        keyboard: {
          enabled: enableKeyboard,
          onlyInViewport: true,
          pageUpDown: false
        },

        breakpoints: {
          750: {
            slidesPerView: slidesTablet,
            spaceBetween: gapDesktop
          },

          990: {
            slidesPerView: slidesDesktop,
            spaceBetween: gapDesktop
          }
        }
      };

      if (
        this.previousButton &&
        this.nextButton
      ) {
        options.navigation = {
          nextEl: this.nextButton,
          prevEl: this.previousButton
        };
      }

      if (this.pagination) {
        options.pagination = {
          el: this.pagination,
          clickable: true
        };
      }

      if (this.progressBar) {
        options.scrollbar = {
          el: this.progressBar,
          draggable: true,
          hide: false
        };
      }

      if (autoRotate && slideCount > 1) {
        options.autoplay = {
          delay: autoRotateDelay,
          disableOnInteraction: false
        };
      }

      this.swiper = new window.Swiper(
        this.slider,
        options
      );

      if (
        autoRotate &&
        this.dataset.pauseOnHover === "true"
      ) {
        this.addEventListener(
          "mouseenter",
          this.handleMouseEnter
        );

        this.addEventListener(
          "mouseleave",
          this.handleMouseLeave
        );
      }
    }

    handleMouseEnter() {
      this.swiper?.autoplay?.pause();
    }

    handleMouseLeave() {
      this.swiper?.autoplay?.resume();
    }

    handleBlockSelect(event) {
      if (
        !this.contains(event.target) ||
        !this.swiper
      ) {
        return;
      }

      const selectedSlide = event.target.closest(
        "[data-article-slide]"
      );

      if (!selectedSlide) return;

      const slides = Array.from(
        this.slider.querySelectorAll(
          ".swiper-slide"
        )
      );

      const slideIndex =
        slides.indexOf(selectedSlide);

      if (slideIndex < 0) return;

      if (this.swiper.params.loop) {
        const realIndex = Number(
          selectedSlide.dataset.swiperSlideIndex
        );

        if (Number.isFinite(realIndex)) {
          this.swiper.slideToLoop(realIndex);
          return;
        }
      }

      this.swiper.slideTo(slideIndex);
    }

    disconnectedCallback() {
      window.clearTimeout(this.swiperTimer);

      document.removeEventListener(
        "shopify:block:select",
        this.handleBlockSelect
      );

      this.removeEventListener(
        "mouseenter",
        this.handleMouseEnter
      );

      this.removeEventListener(
        "mouseleave",
        this.handleMouseLeave
      );

      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
    }
  }

  customElements.define(
    "article-slider",
    ArticleSlider
  );
}